// Permanent deletion, for the OWNER of the app only (migration 0019). The database decides who may do
// this; nothing here is a second check. The owner first LOOKS (preview: what goes, what stays, totals,
// the departments affected, warnings), then confirms with their password, a reason and the word DELETE.
// The delete is tied to the preview by a fingerprint: if anything changed in between, it is refused and
// the owner must look again.
import { supabase } from '@/lib/supabase'
import { FriendlyError } from './auth'
import { DataError, unwrap } from './errors'

/**
 * The kinds of record the owner can start a deletion from: exactly what the screens offer, and what the
 * database accepts. (A dues charge is not deletable on its own, because it would simply be created again:
 * waive it, or delete the member or the plan. A department membership goes with its member.)
 */
export type DeletableTable =
  | 'member'
  | 'dues_plan'
  | 'contribution'
  | 'project'
  | 'collection_sheet'
  | 'budget'
  | 'budget_line'
  | 'expense'
  | 'cash_movement'
  | 'cash_movement_ack'
  | 'income_fund'
  | 'income_session'
  | 'income_line'
  | 'income_transfer'

/** What each kind of record is called on the confirmation screen. */
export const TABLE_NOUNS: Record<string, { one: string; many: string }> = {
  member: { one: 'member', many: 'members' },
  department_membership: { one: 'department membership', many: 'department memberships' },
  dues_plan: { one: 'dues plan', many: 'dues plans' },
  dues_enrollment: { one: 'dues enrolment', many: 'dues enrolments' },
  dues_charge: { one: 'dues charge', many: 'dues charges' },
  dues_allocation: { one: 'dues payment applied to a charge', many: 'dues payments applied to charges' },
  contribution: { one: 'payment', many: 'payments' },
  project: { one: 'project', many: 'projects' },
  collection_sheet: { one: 'collection sheet', many: 'collection sheets' },
  budget: { one: 'budget', many: 'budgets' },
  budget_line: { one: 'budget line', many: 'budget lines' },
  expense: { one: 'expense', many: 'expenses' },
  cash_movement: { one: 'remittance', many: 'remittances' },
  cash_movement_ack: { one: 'record of money received', many: 'records of money received' },
  cash_movement_match: { one: 'match', many: 'matches' },
  reconciliation_exception: { one: 'reconciliation flag', many: 'reconciliation flags' },
  attachment: { one: 'photo', many: 'photos' },
  income_fund: { one: 'fund', many: 'funds' },
  income_session: { one: 'counted service', many: 'counted services' },
  income_line: { one: 'church income amount', many: 'church income amounts' },
  income_transfer: { one: 'transfer to the Conference', many: 'transfers to the Conference' },
}
const noun = (table: string, n: number) => (TABLE_NOUNS[table] ?? { one: table, many: table })[n === 1 ? 'one' : 'many']

/** What links are cleared rather than the record removed, in plain words. */
const DETACH_WORDS: Record<string, string> = {
  'contribution.movement_id': 'no longer marked as sent in a remittance',
  'contribution.sheet_id': 'no longer written on a collection sheet',
  'expense.budget_line_id': 'no longer against a budget line (they become unbudgeted)',
  'member.merged_into_id': 'no longer shown as merged into this member',
}

export interface PreviewItem {
  table: string
  action: 'delete' | 'detach'
  count: number
  col?: string
  totalNgwee: number | null
  firstOn: string | null
  lastOn: string | null
  /** A sentence for the screen, such as "3 payments, K75.00 in total". */
  text: string
}
export interface DeletePreview {
  table: DeletableTable
  id: string
  label: string
  items: PreviewItem[]
  departments: string[]
  warnings: string[]
  /** Hand this back when confirming; it proves the owner is deleting exactly what they were shown. */
  planHash: string
}
type RawItem = { table: string; action: 'delete' | 'detach'; count: number; col?: string; total_ngwee?: number; first_on?: string; last_on?: string }
type RawPreview = { label: string; items: RawItem[]; departments: string[]; warnings: string[]; plan_hash: string }

const kwacha = (ngwee: number) =>
  'K' + (ngwee / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function describe(i: RawItem): string {
  if (i.action === 'detach') {
    return `${i.count} ${noun(i.table, i.count)} will stay, ${DETACH_WORDS[`${i.table}.${i.col}`] ?? 'with a link removed'}`
  }
  const total = i.total_ngwee ? `, ${kwacha(Number(i.total_ngwee))} in total` : ''
  return `${i.count} ${noun(i.table, i.count)}${total}`
}

/** Look at what deleting this would do. Changes nothing. */
export async function previewDelete(table: DeletableTable, id: string): Promise<DeletePreview> {
  const res = await supabase.rpc('owner_delete_preview', { p_table: table, p_id: id })
  const raw = unwrap(res as never, 'Could not look at what would be deleted.') as RawPreview
  return {
    table,
    id,
    label: raw.label,
    items: raw.items.map((i) => ({
      table: i.table,
      action: i.action,
      count: Number(i.count),
      col: i.col,
      totalNgwee: i.total_ngwee === undefined ? null : Number(i.total_ngwee),
      firstOn: i.first_on ?? null,
      lastOn: i.last_on ?? null,
      text: describe(i),
    })),
    departments: raw.departments ?? [],
    warnings: raw.warnings ?? [],
    planHash: raw.plan_hash,
  }
}

export interface DeleteResult {
  label: string
  deleted: Record<string, number>
  detached: Record<string, number>
  /** Proof files that belonged to what was deleted; they are removed from storage next. */
  storagePaths: string[]
  warnings: string[]
}
type RawResult = { label: string; deleted: Record<string, number>; detached: Record<string, number>; storage_paths: string[]; warnings: string[] }

export const MIN_REASON = 10

/**
 * Delete for good. The caller must have confirmed the owner's password just now (see confirmPassword in
 * ./auth): the database refuses unless a password sign-in happened in the last 10 minutes.
 */
export async function deleteRecord(p: {
  table: DeletableTable
  id: string
  reason: string
  confirmWord: string
  planHash: string
}): Promise<DeleteResult> {
  if (p.confirmWord !== 'DELETE') throw new FriendlyError('Type DELETE in capital letters to confirm.')
  if (p.reason.trim().length < MIN_REASON) throw new FriendlyError(`Give a reason of at least ${MIN_REASON} characters.`)
  const res = await supabase.rpc('owner_delete', {
    p_table: p.table,
    p_id: p.id,
    p_reason: p.reason.trim(),
    p_confirm: p.confirmWord,
    p_plan_hash: p.planHash,
  })
  const r = unwrap(res as never, 'Could not delete that.') as RawResult
  const result: DeleteResult = { label: r.label, deleted: r.deleted ?? {}, detached: r.detached ?? {}, storagePaths: r.storage_paths ?? [], warnings: r.warnings ?? [] }
  // The record is gone for good. Removing its stored photos is best effort: if it does not finish now
  // (the tab closes, no connection) the files stay on the owner's list and are removed next time.
  try {
    await removeProofFiles(result.storagePaths)
  } catch {
    /* kept on the list */
  }
  return result
}

/** Photos removed per request: a member with a great many photos must not make one request too large to ever succeed. */
const REMOVE_CHUNK = 100

/** Remove stored proof files that belonged to deleted records, and tell the database which are really gone. */
export async function removeProofFiles(paths: string[]): Promise<void> {
  for (let i = 0; i < paths.length; i += REMOVE_CHUNK) {
    const chunk = paths.slice(i, i + REMOVE_CHUNK)
    const rm = await supabase.storage.from('proofs').remove(chunk)
    if (rm.error) throw new DataError(rm.error, 'Could not remove the stored photos.')
    // The database only marks the ones that are really gone, so a file storage skipped stays on the list.
    const done = await supabase.rpc('owner_files_removed', { p_paths: chunk })
    if (done.error) throw new DataError(done.error, 'Could not record that the photos were removed.')
  }
}

/** Stored photos of deleted records that still need removing (owner only; others see none). */
export async function pendingFileRemovals(): Promise<string[]> {
  const res = await supabase.from('storage_cleanup').select('path').is('removed_at', null).order('queued_at')
  return (unwrap(res as never, 'Could not load the photos waiting to be removed.') as { path: string }[]).map((r) => r.path)
}

/** Try again to remove the photos still waiting. Returns how many were left to do. */
export async function retryFileRemovals(): Promise<number> {
  const paths = await pendingFileRemovals()
  await removeProofFiles(paths)
  return paths.length
}

// ---------------------------------------------------------------- the permanent log
export interface DeletionLogEntry {
  id: string
  deletedAt: string
  deletedBy: string
  table: string
  label: string
  reason: string
  deleted: Record<string, number>
  detached: Record<string, number>
  warnings: string[]
}
type LogRow = {
  id: string
  deleted_at: string
  deleted_by: string
  table_name: string
  label: string
  reason: string
  summary: { deleted?: Record<string, number>; detached?: Record<string, number>; warnings?: string[] }
  deleted_by_name: string
}

/** Every permanent deletion ever made, newest first (the last 200). Administrators and auditors can read it. */
export async function listDeletions(): Promise<DeletionLogEntry[]> {
  const res = await supabase
    .from('owner_deletion')
    .select('id, deleted_at, deleted_by, deleted_by_name, table_name, label, reason, summary')
    .order('deleted_at', { ascending: false })
    .limit(200)
  return (unwrap(res as never, 'Could not load the deletion log.') as unknown as LogRow[]).map((r) => ({
    id: r.id,
    deletedAt: r.deleted_at,
    // the name the owner had when they deleted it: a later rename cannot rewrite history
    deletedBy: r.deleted_by_name,
    table: r.table_name,
    label: r.label,
    reason: r.reason,
    deleted: r.summary?.deleted ?? {},
    detached: r.summary?.detached ?? {},
    warnings: r.summary?.warnings ?? [],
  }))
}

/** "3 payments, 2 dues charges" for a log line. */
export function summariseCounts(counts: Record<string, number>): string {
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .map(([t, n]) => `${n} ${noun(t, n)}`)
    .join(', ')
}
